import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

// Create an axios instance with the base URL
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to add auth token from localStorage
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Categories API calls
const categoryService = {
  // Get all categories
  getAllCategories: async () => {
    try {
      const response = await api.get('/categories');
      return response.data;
    } catch (error) {
      console.error('Error fetching categories:', error);
      throw error;
    }
  },

  // Get category by ID
  getCategoryById: async (id) => {
    try {
      const response = await api.get(`/categories/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching category ${id}:`, error);
      throw error;
    }
  },

  // Create a new category
  createCategory: async (categoryData) => {
    try {
      const response = await api.post('/categories', categoryData);
      return response.data;
    } catch (error) {
      console.error('Error creating category:', error);
      throw error;
    }
  },

  // Update a category
  updateCategory: async (id, categoryData) => {
    try {
      const response = await api.put(`/categories/${id}`, categoryData);
      return response.data;
    } catch (error) {
      console.error(`Error updating category ${id}:`, error);
      throw error;
    }
  },

  // Delete a category
  deleteCategory: async (id) => {
    try {
      const response = await api.delete(`/categories/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting category ${id}:`, error);
      throw error;
    }
  }
};

// Budget Categories API calls (separate from transaction categories)
const budgetCategoryService = {
  // Get all budget categories
  getAllCategories: async () => {
    try {
      const response = await api.get('/api/budget-categories');
      console.log('🔍 Raw budget categories response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching budget categories:', error);
      // Return an empty array instead of default categories
      return [];
    }
  },

  // Create a new budget category
  createCategory: async (categoryData) => {
    try {
      const response = await api.post('/api/budget-categories', categoryData);
      return response.data;
    } catch (error) {
      console.error('Error creating budget category:', error);
      throw error; // Don't create local categories, throw the error
    }
  },

  // Get built-in budget categories
  getDefaultCategories: () => {
    // We'll let the server generate proper ObjectIds for default categories
    return [
      { name: 'Housing', type: 'expense', icon: 'home', color: '#4F46E5' },
      { name: 'Groceries', type: 'expense', icon: 'shopping-cart', color: '#10B981' },
      { name: 'Transportation', type: 'expense', icon: 'car', color: '#F59E0B' },
      { name: 'Utilities', type: 'expense', icon: 'bolt', color: '#6366F1' },
      { name: 'Entertainment', type: 'expense', icon: 'film', color: '#EC4899' },
      { name: 'Dining Out', type: 'expense', icon: 'utensils', color: '#EF4444' },
      { name: 'Shopping', type: 'expense', icon: 'tag', color: '#8B5CF6' },
      { name: 'Health', type: 'expense', icon: 'heartbeat', color: '#06B6D4' }
    ];
  }
};

// Income API calls
const incomeService = {
  // Get all income sources
  getAllIncomes: async (options = {}) => {
    try {
      // Build query string for includePartner parameter
      const queryParams = new URLSearchParams();
      if (options.includePartner) {
        queryParams.append('includePartner', 'true');
      }
      const queryString = queryParams.toString();
      const url = `/api/income${queryString ? `?${queryString}` : ''}`;
      
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching income sources:', error);
      throw error;
    }
  },

  // Get income source by ID
  getIncomeById: async (id) => {
    try {
      const response = await api.get(`/api/income/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching income source ${id}:`, error);
      throw error;
    }
  },

  // Get all income buckets
  getIncomeBuckets: async (options = {}) => {
    try {
      // Build query string for includePartner parameter
      const queryParams = new URLSearchParams();
      if (options.includePartner) {
        queryParams.append('includePartner', 'true');
      }
      const queryString = queryParams.toString();
      const url = `/api/buckets${queryString ? `?${queryString}` : ''}`;
      
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching income buckets:', error);
      // Try the legacy endpoint if the new one fails
      try {
        const legacyQueryParams = new URLSearchParams();
        if (options.includePartner) {
          legacyQueryParams.append('includePartner', 'true');
        }
        const legacyQueryString = legacyQueryParams.toString();
        const legacyUrl = `/api/moneybuckets${legacyQueryString ? `?${legacyQueryString}` : ''}`;
        const legacyResponse = await api.get(legacyUrl);
        return legacyResponse.data;
      } catch (legacyError) {
        console.error('Error fetching from legacy buckets endpoint:', legacyError);
        throw error; // Throw the original error
      }
    }
  },

  // Get incomes in a specific bucket
  getIncomesByBucket: async (bucketId) => {
    try {
      // First try the moneybuckets endpoint
      const response = await api.get(`/api/moneybuckets/${bucketId}/incomes`);
      return response.data && response.data.data ? response.data.data : [];
    } catch (error) {
      console.error(`Error fetching incomes from moneybuckets endpoint: ${error.message}`);
      
      try {
        // Try the second approach: Get bucket details directly which may include incomes
        const bucketResponse = await api.get(`/api/moneybuckets/${bucketId}`);
        const bucket = bucketResponse.data;
        
        if (bucket && bucket.incomes && Array.isArray(bucket.incomes)) {
          console.log(`Found ${bucket.incomes.length} incomes in bucket details`);
          return bucket.incomes;
        }
        
        // If no incomes found in bucket, try getting all incomes and filter by bucket
        const allIncomesResponse = await api.get('/api/income');
        const allIncomes = allIncomesResponse.data && allIncomesResponse.data.data 
          ? allIncomesResponse.data.data 
          : Array.isArray(allIncomesResponse.data) 
            ? allIncomesResponse.data 
            : [];
            
        // Filter incomes that belong to this bucket
        const bucketIncomes = allIncomes.filter(income => 
          income.bucket && 
          (income.bucket === bucketId || 
           income.bucket._id === bucketId || 
           income.bucketId === bucketId)
        );
        
        console.log(`Filtered ${bucketIncomes.length} incomes for bucket ${bucketId}`);
        return bucketIncomes;
      } catch (fallbackError) {
        console.error(`Error with fallback approach for bucket ${bucketId}:`, fallbackError);
        return []; // Return empty array as last resort
      }
    }
  },
};

// Budget API calls
const budgetService = {
  // Get all budgets
  getAllBudgets: async () => {
    try {
      const response = await api.get('/api/budget');
      return response.data;
    } catch (error) {
      console.error('Error fetching budgets:', error);
      throw error;
    }
  },

  // Get budget by ID
  getBudgetById: async (id) => {
    try {
      const response = await api.get(`/api/budget/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching budget ${id}:`, error);
      throw error;
    }
  },

  // Create a new budget
  createBudget: async (budgetData) => {
    try {
      const response = await api.post('/api/budget', budgetData);
      return response.data;
    } catch (error) {
      console.error('Error creating budget:', error);
      throw error;
    }
  },

  // Update a budget
  updateBudget: async (id, budgetData) => {
    try {
      const response = await api.put(`/api/budget/${id}`, budgetData);
      return response.data;
    } catch (error) {
      console.error(`Error updating budget ${id}:`, error);
      throw error;
    }
  },

  // Delete a budget
  deleteBudget: async (id) => {
    try {
      const response = await api.delete(`/api/budget/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting budget ${id}:`, error);
      throw error;
    }
  },

  // Clone a budget
  cloneBudget: async (id) => {
    try {
      const response = await api.post(`/api/budget/${id}/clone`);
      return response.data;
    } catch (error) {
      console.error(`Error cloning budget ${id}:`, error);
      throw error;
    }
  },

  // Get actual spending for a budget
  getActualSpending: async (id) => {
    try {
      const response = await api.get(`/api/budget/${id}/actual`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching actual spending for budget ${id}:`, error);
      throw error;
    }
  },

  // Compare two budgets
  compareBudgets: async (id, compareId) => {
    try {
      const response = await api.get(`/api/budget/${id}/compare?compareId=${compareId}`);
      return response.data;
    } catch (error) {
      console.error(`Error comparing budgets ${id} and ${compareId}:`, error);
      throw error;
    }
  }
};

// Budget Template API calls
const budgetTemplateService = {
  // Get all budget templates
  getAllTemplates: async () => {
    try {
      const response = await api.get('/api/budget-templates');
      return response.data;
    } catch (error) {
      console.error('Error fetching budget templates:', error);
      throw error;
    }
  },

  // Get budget template by ID
  getTemplateById: async (id) => {
    try {
      const response = await api.get(`/api/budget-templates/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching budget template ${id}:`, error);
      throw error;
    }
  },

  // Create a new budget template
  createTemplate: async (templateData) => {
    try {
      const response = await api.post('/api/budget-templates', templateData);
      return response.data;
    } catch (error) {
      console.error('Error creating budget template:', error);
      throw error;
    }
  },

  // Create a template from an existing budget
  createTemplateFromBudget: async (budgetId, templateData) => {
    try {
      const response = await api.post(`/api/budget-templates/from-budget/${budgetId}`, templateData);
      return response.data;
    } catch (error) {
      console.error(`Error creating template from budget ${budgetId}:`, error);
      throw error;
    }
  },

  // Update a budget template
  updateTemplate: async (id, templateData) => {
    try {
      const response = await api.put(`/api/budget-templates/${id}`, templateData);
      return response.data;
    } catch (error) {
      console.error(`Error updating budget template ${id}:`, error);
      throw error;
    }
  },

  // Delete a budget template
  deleteTemplate: async (id) => {
    try {
      const response = await api.delete(`/api/budget-templates/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting budget template ${id}:`, error);
      throw error;
    }
  },

  // Create a budget from a template
  createBudgetFromTemplate: async (id, periodData) => {
    try {
      const response = await api.post(`/api/budget-templates/${id}/create-budget`, periodData);
      return response.data;
    } catch (error) {
      console.error(`Error creating budget from template ${id}:`, error);
      throw error;
    }
  }
};

// Budget Recurrence API calls
const budgetRecurrenceService = {
  // Get all budget recurrences
  getAllRecurrences: async () => {
    try {
      const response = await api.get('/api/budget-recurrences');
      return response.data;
    } catch (error) {
      console.error('Error fetching budget recurrences:', error);
      throw error;
    }
  },

  // Get budget recurrence by ID
  getRecurrenceById: async (id) => {
    try {
      const response = await api.get(`/api/budget-recurrences/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching budget recurrence ${id}:`, error);
      throw error;
    }
  },

  // Create a new budget recurrence
  createRecurrence: async (recurrenceData) => {
    try {
      const response = await api.post('/api/budget-recurrences', recurrenceData);
      return response.data;
    } catch (error) {
      console.error('Error creating budget recurrence:', error);
      throw error;
    }
  },

  // Update a budget recurrence
  updateRecurrence: async (id, recurrenceData) => {
    try {
      const response = await api.put(`/api/budget-recurrences/${id}`, recurrenceData);
      return response.data;
    } catch (error) {
      console.error(`Error updating budget recurrence ${id}:`, error);
      throw error;
    }
  },

  // Delete a budget recurrence
  deleteRecurrence: async (id) => {
    try {
      const response = await api.delete(`/api/budget-recurrences/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting budget recurrence ${id}:`, error);
      throw error;
    }
  },

  // Manually create a budget from a recurrence
  createBudgetFromRecurrence: async (id) => {
    try {
      const response = await api.post(`/api/budget-recurrences/${id}/create-budget`);
      return response.data;
    } catch (error) {
      console.error(`Error creating budget from recurrence ${id}:`, error);
      throw error;
    }
  },

  // Get all budgets created from a recurrence
  getBudgetsFromRecurrence: async (id) => {
    try {
      const response = await api.get(`/api/budget-recurrences/${id}/budgets`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching budgets from recurrence ${id}:`, error);
      throw error;
    }
  },

  // Toggle active status of a recurrence
  toggleRecurrenceActive: async (id) => {
    try {
      const response = await api.patch(`/api/budget-recurrences/${id}/toggle-active`);
      return response.data;
    } catch (error) {
      console.error(`Error toggling active status for recurrence ${id}:`, error);
      throw error;
    }
  }
};

// Utility function to validate MongoDB ObjectIds
export const isValidObjectId = (id) => {
  const objectIdPattern = /^[0-9a-fA-F]{24}$/;
  return objectIdPattern.test(id);
};

export { api, budgetService, budgetTemplateService, budgetRecurrenceService, categoryService, budgetCategoryService, incomeService }; 